import type { SegmentId } from "@p2p-hls/protocol";
import type { P2PEngine } from "./engine.js";
import type { HttpFetch } from "./scheduler.js";

/**
 * hls.js custom-loader adapter. Assigning the returned class to
 * `hls.config.fLoader` routes every fragment load through the P2P engine while
 * leaving playlists/keys/init-segments on plain HTTP.
 *
 * Kept structurally typed (no `hls.js` import) so core-engine stays free of a
 * player dependency — the native/TV integrations provide their own adapters.
 */

interface LoaderContext {
  url: string;
  responseType: string;
  rangeStart?: number;
  rangeEnd?: number;
  frag?: { sn: number | "initSegment"; start?: number; duration?: number };
}

interface LoaderStats {
  aborted: boolean;
  loaded: number;
  total: number;
  retry: number;
  chunkCount: number;
  bwEstimate: number;
  loading: { start: number; first: number; end: number };
  parsing: { start: number; end: number };
  buffering: { start: number; first: number; end: number };
}

interface LoaderCallbacks {
  onSuccess: (
    response: { url: string; data: ArrayBuffer | string },
    stats: LoaderStats,
    context: LoaderContext,
    networkDetails: unknown,
  ) => void;
  onError: (
    error: { code: number; text: string },
    context: LoaderContext,
    networkDetails: unknown,
    stats: LoaderStats,
  ) => void;
  onTimeout: (stats: LoaderStats, context: LoaderContext, networkDetails: unknown) => void;
  onAbort?: (stats: LoaderStats, context: LoaderContext, networkDetails: unknown) => void;
}

export interface P2PLoaderOptions {
  /** Current playback position (seconds); used to compute the segment deadline. */
  getCurrentTime: () => number;
}

function newStats(): LoaderStats {
  return {
    aborted: false,
    loaded: 0,
    total: 0,
    retry: 0,
    chunkCount: 0,
    bwEstimate: 0,
    loading: { start: 0, first: 0, end: 0 },
    parsing: { start: 0, end: 0 },
    buffering: { start: 0, first: 0, end: 0 },
  };
}

function toArrayBuffer(u: Uint8Array): ArrayBuffer {
  return u.byteOffset === 0 && u.byteLength === u.buffer.byteLength
    ? (u.buffer as ArrayBuffer)
    : u.slice().buffer;
}

/** Build the hls.js loader class bound to a given engine instance. */
export function createP2PFragmentLoader(engine: P2PEngine, options: P2PLoaderOptions) {
  return class P2PFragmentLoader {
    #aborted = false;
    readonly #abortController = new AbortController();
    stats = newStats();

    // hls.js instantiates loaders with the loader config; we ignore it here.
    constructor(_config: unknown) {}

    load(context: LoaderContext, _config: unknown, callbacks: LoaderCallbacks): void {
      this.stats.loading.start = now();
      const httpFetch: HttpFetch = (url, signal) => fetchBytes(url, context, signal);

      // hls.js sets rangeStart/rangeEnd to 0 (not null) for non-ranged loads —
      // a real byte range only exists when rangeEnd > rangeStart.
      const isFragment =
        !!context.frag &&
        context.responseType === "arraybuffer" &&
        typeof context.frag.sn === "number" &&
        !hasByteRange(context); // byte-range frags bypass P2P (URL-keyed dedup)

      if (!isFragment) {
        // Playlists, keys, init segments, byte-range frags: straight to HTTP.
        fetchTyped(context, this.#abortController.signal)
          .then((data) => this.#succeed(context, data, callbacks))
          .catch((err) => this.#fail(context, err, callbacks));
        return;
      }

      const frag = context.frag!;
      const seg: SegmentId = { url: context.url, sn: frag.sn as number };
      const deadlineMs = Math.max(0, ((frag.start ?? 0) - options.getCurrentTime()) * 1000);

      engine
        .getSegment(seg, deadlineMs, httpFetch)
        .then((res) => this.#succeed(context, toArrayBuffer(res.data), callbacks))
        .catch((err) => this.#fail(context, err, callbacks));
    }

    abort(): void {
      this.#aborted = true;
      this.stats.aborted = true;
      this.#abortController.abort();
    }

    destroy(): void {
      this.abort();
    }

    #succeed(context: LoaderContext, data: ArrayBuffer | string, callbacks: LoaderCallbacks): void {
      if (this.#aborted) return;
      const len = typeof data === "string" ? data.length : data.byteLength;
      this.stats.loaded = len;
      this.stats.total = len;
      this.stats.loading.first = this.stats.loading.first || now();
      this.stats.loading.end = now();
      callbacks.onSuccess({ url: context.url, data }, this.stats, context, null);
    }

    #fail(context: LoaderContext, err: unknown, callbacks: LoaderCallbacks): void {
      if (this.#aborted) return;
      callbacks.onError({ code: 0, text: err instanceof Error ? err.message : String(err) }, context, null, this.stats);
    }
  };
}

/** True when the loader context specifies a real byte range (rangeEnd > rangeStart). */
function hasByteRange(context: LoaderContext): boolean {
  return (context.rangeEnd ?? 0) > (context.rangeStart ?? 0);
}

/** Fetch bytes honoring an optional byte range from the loader context. */
async function fetchBytes(url: string, context: LoaderContext, signal?: AbortSignal): Promise<Uint8Array> {
  const headers: Record<string, string> = {};
  if (hasByteRange(context)) {
    headers["Range"] = `bytes=${context.rangeStart}-${context.rangeEnd! - 1}`;
  }
  const res = await fetch(url, { headers, signal });
  if (!res.ok && res.status !== 206) throw new Error(`HTTP ${res.status} for ${url}`);
  return new Uint8Array(await res.arrayBuffer());
}

/** Fetch respecting responseType (string for playlists, ArrayBuffer otherwise). */
async function fetchTyped(context: LoaderContext, signal?: AbortSignal): Promise<ArrayBuffer | string> {
  const bytes = await fetchBytes(context.url, context, signal);
  if (context.responseType === "arraybuffer") return toArrayBuffer(bytes);
  return new TextDecoder().decode(bytes);
}

function now(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}
